import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import axios from "../../helpers/axiosConfig";
import ParticlesAuth from "./ParticlesAuth";

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_STORAGE_KEY = "emailVerificationResendAt";

const getStoredResendAt = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  const stored = window.localStorage.getItem(RESEND_STORAGE_KEY);
  const parsed = stored ? Number(stored) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const getRemainingSeconds = (nextAllowedAt: number) =>
  Math.max(0, Math.ceil((nextAllowedAt - Date.now()) / 1000));

const VerifyEmail = () => {
  const { user } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const [nextAllowedAt, setNextAllowedAt] = useState(getStoredResendAt);
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    getRemainingSeconds(nextAllowedAt),
  );
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "danger" | "info";
    message: string;
  } | null>(null);

  const canResend = useMemo(
    () => remainingSeconds === 0 && !isSending,
    [remainingSeconds, isSending],
  );

  useEffect(() => {
    document.title = "Hệ thống quản lý đặt món - Xác minh email";
  }, []);

  useEffect(() => {
    if (user?.email_verified_at) {
      navigate("/order", { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRemainingSeconds(getRemainingSeconds(nextAllowedAt));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [nextAllowedAt]);

  const handleResend = async () => {
    if (!canResend) return;

    setIsSending(true);
    setStatus(null);

    try {
      await axios.post("/api/email/verification-notification");

      const nextAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      setNextAllowedAt(nextAt);
      setRemainingSeconds(getRemainingSeconds(nextAt));
      window.localStorage.setItem(RESEND_STORAGE_KEY, String(nextAt));

      setStatus({
        type: "success",
        message: "Đã gửi lại email xác minh. Vui lòng kiểm tra hộp thư của bạn.",
      });
    } catch (error: any) {
      const statusCode = error?.response?.status ?? error?.status;
      let message = "Không thể gửi lại email xác minh. Vui lòng thử lại.";

      if (statusCode === 429) {
        message = "Bạn đã yêu cầu quá nhiều lần. Vui lòng chờ ít phút rồi thử lại.";
      }

      if (statusCode === 401 || statusCode === 419) {
        message = "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.";
      }

      setStatus({ type: "danger", message });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ParticlesAuth>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-10 col-lg-8">
            <div className="text-center text-white mb-4">
              <h2 className="text-white">Xác minh email</h2>
              <p className="text-white-50 mb-0">
                Chúng tôi đã gửi email xác minh. Vui lòng kiểm tra email.
              </p>
            </div>

            {!user && (
              <Alert color="warning" className="text-start">
                Vui lòng đăng nhập để tiếp tục xác minh email. {" "}
                <Link to="/login" className="text-warning text-decoration-underline">
                  Đăng nhập
                </Link>
              </Alert>
            )}

            {status && <Alert color={status.type}>{status.message}</Alert>}

            <div className="bg-white rounded p-4">
              <p className="text-muted mb-3">
                {user?.email
                  ? `Email đã đăng ký: ${user.email}`
                  : "Bạn cần đăng nhập để gửi lại email xác minh."}
              </p>

              <Button
                className="btn btn-warning btn-lg w-100"
                disabled={!canResend || !user}
                onClick={handleResend}
              >
                {isSending && <Spinner size="sm" className="me-2" />}
                {remainingSeconds > 0
                  ? `Gửi lại sau ${remainingSeconds}s`
                  : "Gửi lại email xác minh"}
              </Button>

              <div className="text-center mt-3">
                <Link className="text-muted" to="/logout">
                  Đăng xuất
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ParticlesAuth>
  );
};

export default VerifyEmail;
